import { useState, useEffect } from "react";
import Api from "../../api/api";
import stringHelpers from "../../../utilities/stringHelpers";
import { dateRange } from "../../../utilities/dateUtilities";
import { ScheduleExportDateRangePicker, isScheduleDateRangeCustomRangeInvalid } from "../scheduleExportDateRangePicker";
import { SearchSelect } from "../../shared/searchSelect";
import { ScheduleExportModal } from "../../shared/scheduleExportModal";
import { TagSelector } from "../../shared/tagSelector";
import arrayUtilities from "../../../utilities/arrayUtilities";

export const pcnsPaidReportType = 6

export const PcnsPaidScheduleModal = ({ sites, clients, tags = [], organisationId, token, onCancel, onSaved, scheduledExport, initialParameters, initialSitesByClient }) => {
    const seedParameters = scheduledExport?.parameters ?? initialParameters ?? {}
    const hasCustomDates = seedParameters.from != null || seedParameters.to != null
    const isEditing = !stringHelpers.isNullOrEmpty(scheduledExport?.id)

    const [scheduleFromDate, setScheduleFromDate] = useState(seedParameters.from ?? dateRange("lastMonth").firstDate)
    const [scheduleToDate, setScheduleToDate] = useState(seedParameters.to ?? dateRange("lastMonth").lastDate)
    const [scheduledDateRangeTypePreset, setScheduledDateRangeTypePreset] = useState(seedParameters.reportDatePreset || (hasCustomDates ? null : 0))
    const [scheduleSelectedClient, setScheduleSelectedClient] = useState(seedParameters.clientId ?? "")
    const [scheduleSitesByClient, setScheduleSitesByClient] = useState(initialSitesByClient ?? sites)
    const [scheduleSelectedSiteByClient, setScheduleSelectedSiteByClient] = useState(seedParameters.siteId ?? "")
    const [schedulePcnsGroupBy, setSchedulePcnsGroupBy] = useState(seedParameters.pcnPaidGroupBy ?? 0)
    const [scheduleSelectedTags, setScheduleSelectedTags] = useState(seedParameters.tagIds ?? [])
    const [isSaving, setIsSaving] = useState(false)
    const [saveErrorMessage, setSaveErrorMessage] = useState(null)

    const isScheduleCustomDateRangeInvalid = isScheduleDateRangeCustomRangeInvalid(scheduledDateRangeTypePreset, scheduleFromDate, scheduleToDate)
    const isSubmitDisabled = isScheduleCustomDateRangeInvalid || isSaving

    useEffect(() => {
        const needsSavedClientSites = !initialSitesByClient && !stringHelpers.isNullOrEmpty(seedParameters.clientId)
        if (needsSavedClientSites) {
            Api.getClientSites(
                data => setScheduleSitesByClient(data),
                error => console.log(error),
                seedParameters.clientId,
                organisationId,
                token
            )
        }
    }, [])

    const handleScheduleClientSelect = (value) => {
        setScheduleSelectedClient(value)
        if (stringHelpers.isNullOrEmpty(value)) {
            setScheduleSitesByClient(sites)
            setScheduleSelectedSiteByClient("")
        } else {
            Api.getClientSites(
                data => {
                    setScheduleSitesByClient(data)
                    setScheduleSelectedSiteByClient("")
                },
                error => console.log(error),
                value,
                organisationId,
                token
            )
        }
    }

    const buildScheduledExportParameters = () => {
        const isUsingDateRangePreset = scheduledDateRangeTypePreset !== null
        const dateRangeParameters = isUsingDateRangePreset
            ? { reportDatePreset: scheduledDateRangeTypePreset }
            : { from: scheduleFromDate, to: scheduleToDate }

        return {
            ...dateRangeParameters,
            siteId: !stringHelpers.isNullOrEmpty(scheduleSelectedSiteByClient) ? scheduleSelectedSiteByClient : null,
            clientId: !stringHelpers.isNullOrEmpty(scheduleSelectedClient) ? scheduleSelectedClient : null,
            pcnPaidGroupBy: schedulePcnsGroupBy,
            tagIds: !arrayUtilities.isNullOrEmpty(scheduleSelectedTags) ? scheduleSelectedTags : null,
        }
    }

    const handleSaveFailed = (error) => {
        setSaveErrorMessage(error?.errorMessage ?? "Unable to save scheduled export")
        setIsSaving(false)
    }

    const handleSaveSucceeded = (data) => {
        if (data?.isError) {
            handleSaveFailed(data)
        } else {
            onSaved()
        }
    }

    return (
        <ScheduleExportModal
            initialValues={ scheduledExport }
            reportType={ pcnsPaidReportType }
            onCancel={ onCancel }
            disableConfirm={ isSubmitDisabled }
            errorMessage={ saveErrorMessage }
            onSubmit={ (isActive, name, scheduleFrequencyMode, monday, tuesday, wednesday, thursday, friday, saturday, sunday, dayOfMonth, time, recipients) => {
                setIsSaving(true)
                setSaveErrorMessage(null)
                if (isEditing) {
                    Api.updateScheduledExport(
                        handleSaveSucceeded,
                        handleSaveFailed,
                        scheduledExport.id,
                        name, pcnsPaidReportType, scheduleFrequencyMode,
                        monday,
                        tuesday,
                        wednesday,
                        thursday,
                        friday,
                        saturday,
                        sunday,
                        time,
                        dayOfMonth,
                        recipients,
                        buildScheduledExportParameters(),
                        isActive,
                        organisationId, token
                    )
                } else {
                    Api.createScheduledExport(
                        handleSaveSucceeded,
                        handleSaveFailed,
                        name, pcnsPaidReportType, scheduleFrequencyMode,
                        monday,
                        tuesday,
                        wednesday,
                        thursday,
                        friday,
                        saturday,
                        sunday,
                        time,
                        dayOfMonth,
                        recipients,
                        buildScheduledExportParameters(),
                        isActive,
                        organisationId, token
                    )
                }
            } }
        >
            <div className="">
                <h5 className="mb-2">Filters</h5>
                <div className="row mb-2">
                    <div className="col-md-3">Date Range</div>
                    <div className="col-md-9">
                        <ScheduleExportDateRangePicker
                            scheduledDateRangeTypePreset={ scheduledDateRangeTypePreset }
                            setScheduledDateRangeTypePreset={ setScheduledDateRangeTypePreset }
                            scheduleFromDate={ scheduleFromDate }
                            setScheduleFromDate={ setScheduleFromDate }
                            scheduleToDate={ scheduleToDate }
                            setScheduleToDate={ setScheduleToDate }
                        />
                    </div>
                </div>
                <div className="row mb-2">
                    <div className="col-md-3">Client</div>
                    <div className="col-md-9">
                        <SearchSelect
                            options={ [{ value: "", label: "All" }, ...clients.map(item => ({ value: item.id, label: item.name }))] }
                            onSelect={ handleScheduleClientSelect }
                            selected={ scheduleSelectedClient }
                            defaultLabel="All"
                        />
                    </div>
                </div>
                <div className="row mb-2">
                    <div className="col-md-3">Site</div>
                    <div className="col-md-9">
                        <SearchSelect
                            width="100%"
                            options={ [{ value: "", label: "All" }, ...scheduleSitesByClient.map(item => ({ value: item.id, label: item.name }))] }
                            onSelect={ value => setScheduleSelectedSiteByClient(value) }
                            selected={ scheduleSelectedSiteByClient }
                            defaultLabel="All"
                        />
                    </div>
                </div>
                { !arrayUtilities.isNullOrEmpty(tags) && <div className="row mb-2">
                    <div className="col-md-3">Tags</div>
                    <div className="col-md-9">
                        <TagSelector
                            onChange={ tagSelections => setScheduleSelectedTags(tagSelections) }
                            selectedTags={ scheduleSelectedTags }
                            tags={ tags }
                        />
                    </div>
                </div> }
                <div className="row mb-2">
                    <div className="col-md-3">Group By</div>
                    <div className="col-md-9">
                        <select
                            id="group-by-selector"
                            className="border rounded py-1 px-2 w-fit"
                            onChange={ (e) => setSchedulePcnsGroupBy(Number(e.target.value)) }
                            value={ String(schedulePcnsGroupBy) }
                        >
                            <option value="0">Site and Amount</option>
                            <option value="1">Site</option>
                            <option value="2">Tag</option>
                            <option value="3">Tag and Site</option>
                        </select>
                    </div>
                </div>
            </div>
            <hr className="border-dashed border-bottom-0"></hr>
        </ScheduleExportModal>
    )
}
